/* eslint-disable no-nested-ternary */
import { useEffect } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { useHandleFormErrors } from '@app/utils';
import { useTokenData, useTokenUserData, useCreateNewTeam } from '@data/hooks';
import { APIError } from '@data/index';
import { Role, TeamBody } from '@data/sdk/match';
import { useAppUserStore } from '@data/storage';
import { AntdForm } from '@ui/components';
import { useSignUpTracking } from '../../../hooks';
import { useRefreshTeam } from './useRefreshTeam';

type CreateTeamFormValues = {
  name?: string | null;
  organizationName?: string | null;
  researchSiteName?: string | null;
  teamRole?: string | null;
};

export const useCreateTeam = () => {
  const [token] = useQueryParam('token', StringParam);
  const [form] = AntdForm.useForm<CreateTeamFormValues>();

  const { user } = useAppUserStore();
  const { userId } = useTokenUserData();

  const { data: tokenData } = useTokenData(token ?? '');
  const { mutate: createTeam } = useCreateNewTeam();
  const { mutate: refreshSession } = useRefreshTeam(!!token);

  const { trackCreateTeam } = useSignUpTracking();

  const initialValues: CreateTeamFormValues = {
    name: tokenData?.company ?? '',
    teamRole: tokenData?.companyRole ?? '',
    organizationName: '',
    researchSiteName: '',
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [tokenData]);

  const handleCreateTeam = (values: CreateTeamFormValues) => {
    const transformedValues: TeamBody = {
      userId: userId ?? '',
      name: values.name ?? '',
      role: tokenData?.role ?? Role.TrialManager,
      organizationName: values.organizationName ?? '',
      researchSiteName: values.researchSiteName ?? '',
      teamRole: values.teamRole ?? '',
      ...(token && { token }),
    };

    createTeam(transformedValues, {
      onSuccess: async (team) => {
        trackCreateTeam(values.teamRole ?? '');
        if (user) refreshSession({ teamId: team.id, refreshToken: user?.refreshToken });
      },
      onError: (error: APIError<CreateTeamFormValues>) => useHandleFormErrors(error, form),
    });
  };

  return {
    form,
    tokenData,
    initialValues,
    handleCreateTeam,
  };
};
